(ns practitest.frontend.ptql-editor.autocomplete
  (:require
    [clojure.string :as str]
    [practitest.frontend.ptql.parser :as ptql-parser]))

(defn- extract-last-field
  "Extract last field name (quoted or not) matching the given pattern."
  [text pattern]
  (when-let [m (re-find pattern text)]
    (or (nth m 1 nil) (nth m 2 nil))))

(defn- find-field-before-cursor [text]
  (extract-last-field text #"(?:'([^']+)'|(\w+))\s+(?:IS|=|!=|>=?|<=?)\s*$"))

(defn- linked-entity-in-scope
  "If cursor is inside a `LINKED TO <Entity> WHERE (...)` clause, return the entity name."
  [text]
  (loop [i (dec (count text)) depth 0]
    (cond
      (neg? i) nil
      (= (.charAt text i) \)) (recur (dec i) (inc depth))
      (= (.charAt text i) \() (if (zero? depth)
                                (when-let [m (re-find #"LINKED\s+TO\s+(\w+)\s+WHERE\s*$"
                                                      (subs text 0 i))]
                                  (nth m 1))
                                (recur (dec i) (dec depth)))
      :else (recur (dec i) depth))))

(defn- find-field-before-operator [text]
  (extract-last-field (str/trimr text) #"(?:'([^']+)'|(\w+))\s*$"))

(defn- keyword->completion [[op boost]]
  {:label op :type "keyword" :boost boost})

(defn- field->completion [field]
  {:label (if (str/includes? (:name field) " ")
            (str "'" (:name field) "'")
            (:name field))
   :type "variable"
   :detail (get field :type "")})

(defn- value->completion [value]
  {:label (str "\"" value "\"") :type "value"})

(defn- operators-per-field-type [field-type]
  (case field-type
    ("user" "list") (mapv keyword->completion [["IS" 2] ["IS EMPTY" 2] ["IS NOT EMPTY" 2]
                                               ["=" 1] ["!=" 1]])
    "text" (mapv keyword->completion [["IS" 2] ["IS EMPTY" 2] ["IS NOT EMPTY" 2] ["STARTS WITH" 2] ["CONTAINS" 2]
                                      ["=" 1] ["!=" 1]])
    ("number" "date-time") (mapv keyword->completion [["IS EMPTY" 2] ["IS NOT EMPTY" 2] ["IN LAST" 2] ["NOT IN LAST" 2]
                                                      ["=" 1] ["!=" 1] [">" 1] [">=" 1] ["<" 1] ["<=" 1]])
    ;; fallback
    (mapv keyword->completion
          [["IS" 2] ["IS EMPTY" 2] ["IS NOT EMPTY" 2] ["STARTS WITH" 2] ["CONTAINS" 2] ["IN LAST" 2] ["NOT" 2]
           ["=" 1] ["!=" 1] [">" 1] [">=" 1] ["<" 1] ["<=" 1]])))

(defn- field-by-name [fields name]
  (first (filter #(= (str/lower-case (:name %)) (str/lower-case name)) fields)))

(defn- expects-field-name? [expected]
  (or (contains? expected "field-name")
      (some #(str/includes? % "[a-zA-Z_]") expected)))

(defn- expects-value? [expected]
  (or (contains? expected "value")
      ;; NOTE: matches double-quoted string regex from grammar
      (some #(str/includes? % "[^\\\"]*") expected)))

(defn- field-name-completions [expected fields]
  (when (expects-field-name? expected)
    (map field->completion fields)))

(defn- field-value-completions [expected text-to-parse fields users groups]
  (when (expects-value? expected)
    (if (str/ends-with? (str/trimr text-to-parse) "GROUP")
      (map (comp value->completion :name) groups)
      (when-let [field (some->> (find-field-before-cursor text-to-parse)
                                (field-by-name fields))]
        (cond
          (= (:type field) "user")
          (concat
            (map (comp value->completion :email) users)
            [{:label "CURRENT_USER" :type "value" :boost 2}
             {:label "GROUP" :type "keyword" :boost 2}])

          (seq (:values field))
          (map value->completion (:values field)))))))

(defn- operator-completions [expected text-to-parse fields]
  (when (some #(contains? expected %) ["IS" "=" "!=" ">" ">=" "<" "<=" "STARTS" "CONTAINS" "IN" "NOT"])
    (when-let [field (some->> (find-field-before-operator text-to-parse)
                              (field-by-name fields))]
      (operators-per-field-type (:type field)))))

(defn- keyword-completions [expected]
  (concat
    (keep (fn [[k label]]
            (when (contains? expected k)
              (keyword->completion [label 2])))
          [["LINKED" "LINKED TO"]])
    (keep (fn [k]
            (when (contains? expected k)
              (keyword->completion [k 2])))
          ["WHERE" "AND" "OR" "Requirement" "Test" "TestSet" "Issue"
           "day" "days" "month" "months" "COUNT"])))

(defn completion-source [fields-by-entity source-entity users groups]
  (fn [^js context]
    (let [word (.matchBefore context #"[\w\"']+")
          from (if word (.-from word) (.-pos context))
          text-to-parse (.sliceString (.-doc (.-state context)) 0 from)
          scope-entity (or (linked-entity-in-scope text-to-parse) source-entity)
          fields (get fields-by-entity scope-entity [])
          result (ptql-parser/parse text-to-parse)]
      (when (or word (.-explicit context))
        (clj->js
          {:from from
           :options (if (ptql-parser/failure? result)
                      (let [expected (ptql-parser/get-expected result)]
                        (concat
                          (field-name-completions expected fields)
                          (field-value-completions expected text-to-parse fields users groups)
                          (operator-completions expected text-to-parse fields)
                          (keyword-completions expected)))
                      (concat
                        (when (re-find #"LINKED\s+TO\s+\w+\s*$" text-to-parse)
                          [(keyword->completion ["WHERE" 3])])
                        (map keyword->completion [["AND" 2] ["OR" 2]])))})))))
